import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { latestBeatIndex } from "../state/markers";
import { weaveBeats } from "../state/weave";
import type { WeaveFilter } from "../state/weave";
import type { AgentStoryPayload, ReplayPayload } from "../types";

const FILTERS: { value: WeaveFilter; label: string }[] = [
  { value: "all", label: "all" },
  { value: "near", label: "near her" },
  { value: "off", label: "off" },
];

// Session-scoped: refocusing an agent never refetches, and the weave filter is
// remembered across focus changes.
const storyCache = new Map<string, AgentStoryPayload>();
let rememberedFilter: WeaveFilter = "all";

export interface AgentStoryPanelProps {
  runId: string;
  agent: number;
  replay: ReplayPayload;
  tick: number;
  onSeek: (tick: number) => void;
  onClear: () => void;
}

export function AgentStoryPanel({
  runId,
  agent,
  replay,
  tick,
  onSeek,
  onClear,
}: AgentStoryPanelProps) {
  const [story, setStory] = useState<AgentStoryPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [filter, setFilterState] = useState<WeaveFilter>(rememberedFilter);
  const setFilter = (next: WeaveFilter) => {
    rememberedFilter = next;
    setFilterState(next);
  };

  useEffect(() => {
    const key = `${runId}:${agent}`;
    const cached = storyCache.get(key);
    if (cached) {
      setStory(cached);
      setError(null);
      return;
    }
    let stale = false;
    setStory(null);
    setError(null);
    api
      .agentStory(runId, agent)
      .then((payload) => {
        storyCache.set(key, payload);
        if (!stale) setStory(payload);
      })
      .catch((fetchError: unknown) => {
        if (!stale) setError(String(fetchError));
      });
    return () => {
      stale = true;
    };
  }, [runId, agent, attempt]);

  const woven = useMemo(
    () =>
      story
        ? weaveBeats(story.beats, replay.beats, filter, replay.frames, agent, replay.grid.width)
        : [],
    [story, replay, filter, agent],
  );
  const current = latestBeatIndex(woven, tick);
  const currentRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: "nearest" });
  }, [current]);

  return (
    <aside className="agent-panel">
      <div className="agent-panel-header">
        <button type="button" className="focus-chip" onClick={onClear} title="clear focus">
          agent {agent} ⨯
        </button>
        <span className="weave-toggle" role="radiogroup" aria-label="settlement beats">
          settlement:
          {FILTERS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={filter === value}
              className={filter === value ? "active" : ""}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </span>
      </div>
      {!story && !error && <div className="pane-empty">reading her story…</div>}
      {error && (
        <div className="pane-empty">
          {error}{" "}
          <button type="button" className="btn btn-ghost" onClick={() => setAttempt(attempt + 1)}>
            retry
          </button>
        </div>
      )}
      {story && (
        <div className="story-list">
          {woven.length === 0 && <div className="pane-empty">a quiet life</div>}
          {woven.map((beat, index) => (
            <button
              key={index}
              type="button"
              className={`story-beat ${beat.source === "settlement" ? "beat-settlement" : ""} ${
                current !== null && index === current ? "current" : beat.tick <= tick ? "passed" : ""
              }`}
              onClick={() => onSeek(beat.tick)}
              title="jump the replay to this beat"
              ref={current !== null && index === current ? currentRef : null}
            >
              <span className="story-tick">tick {beat.tick}</span>
              <span>
                {beat.source === "settlement" ? "◦ " : ""}
                {beat.text}.
              </span>
            </button>
          ))}
          <div className="story-closing">{story.closing}.</div>
        </div>
      )}
    </aside>
  );
}
